/**
 * Frido Combo Offer Rows runtime — renders the combo-offer contract only
 * (pool.combo = the spectrum_combo_offer projection emitted by the wrapper).
 * Rows are member products; picking a row swaps the displayed product IN
 * PLACE via `spectrum:buying-option-change` — never navigates.
 *
 * The chip on a row = authored badge name + computed discount % — either part
 * alone still renders ("Newly Launched 64% OFF" / bare "66% OFF"). Only the
 * default (highlighted) row carries the computed %, matching how the source
 * storefront decorates the combo row only.
 *
 * Optional self-contained member mini-PDP drawer (prop `member_quick_add`):
 * when on, the page's Add-to-Cart is intercepted (capture phase) and opens an
 * in-snippet <dialog> for the SELECTED member so the shopper picks that
 * member's variant before adding. Fetches `/products/{handle}.js`, renders the
 * option rows, resolves the variant, and adds via `/cart/add.js` + a
 * `cart:update` event so the theme cart drawer opens. No dependence on the
 * theme quick-add — shell, styles and this logic all live in the snippet.
 */
;(() => {
  if (window.__sai_fcbfrd03_initialized__) return
  window.__sai_fcbfrd03_initialized__ = true

  const SNIPPET_ID = 'fcbfrd03'
  const TAG = 'sai-fcbfrd03'
  const FEATURE_SLUG = 'quantity_bundles'

  function esc(s) {
    return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
  }
  function readPool(node) {
    for (let i = 0; i < node.children.length; i++) {
      const c = node.children[i]
      if (c.tagName === 'SCRIPT' && c.hasAttribute('data-spectrum-snippet-pool')) {
        try { return JSON.parse(c.textContent) } catch (e) { return {} }
      }
    }
    return {}
  }
  function fmtMoney(major, cur) {
    if (major == null || Number.isNaN(Number(major))) return ''
    return (cur || '₹') + Math.round(Number(major)).toLocaleString('en-IN')
  }
  // Minor-units (Shopify Ajax API returns paise/cents) → "₹1,299"
  function fmtMinor(minor, cur) {
    if (minor == null || Number.isNaN(Number(minor))) return ''
    return (cur || '₹') + Math.round(Number(minor) / 100).toLocaleString('en-IN')
  }
  function timerState(key, minutes) {
    const total = minutes * 60 * 1000
    let start = 0
    try { start = Number(localStorage.getItem(key)) || 0 } catch (e) {}
    const now = Date.now()
    if (!start || now - start >= total) {
      start = now
      try { localStorage.setItem(key, String(start)) } catch (e) {}
    }
    return { remaining: total - (now - start), total }
  }
  function fmtClock(ms) {
    const s = Math.max(0, Math.floor(ms / 1000))
    const h = String(Math.floor(s / 3600)).padStart(2, '0')
    const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0')
    const sec = String(s % 60).padStart(2, '0')
    return `${h}<small>h</small>&nbsp; ${m}<small>m</small> ${sec}<small>s</small>`
  }

  function buildRows(pool) {
    const combo = pool.combo
    if (!combo || !Array.isArray(combo.members) || combo.members.length < 1) return []
    return combo.members.map((m, i) => {
      const price = m.price != null ? m.price / 100 : null
      const mrp = m.compare_at && m.compare_at > m.price ? m.compare_at / 100 : null
      const save = mrp && price != null ? mrp - price : null
      let badge = m.badge || ''
      if (m.default && save && mrp) {
        const pct = Math.round((save / mrp) * 100)
        badge = badge ? `${badge} ${pct}% OFF` : `${pct}% OFF`
      }
      return {
        idx: i,
        title: m.label,
        price, mrp, save,
        image: m.image || null,
        handle: m.handle || null,
        variantId: m.first_variant_id || null,
        multiVariant: !!m.multi_variant,
        current: !!m.current,
        selected: !!m.default,
        timerMin: Number(m.timer) || 0,
        badge,
      }
    })
  }

  // ── Member mini-PDP drawer controller ───────────────────────────────────
  // Bound to one <dialog data-sai-fcbfrd03-qa>. `open(handle, currency)` fetches
  // the product, renders its options, and drives variant selection + ATC.
  const isColorOpt = (name) => /^colou?r$/i.test(String(name || '').trim())
  const COLOR_HEX = (name) => {
    const v = String(name || '').toLowerCase()
    if (/(black|onyx|jet|charcoal)/.test(v)) return '#1a1a1a'
    if (/(white|ivory|cream|pearl)/.test(v)) return '#f3f1ec'
    if (/(grey|gray|graphite|silver)/.test(v)) return '#7a7a7a'
    if (/(blue|navy|sky|aqua|teal)/.test(v)) return '#1976d2'
    if (/(red|crimson|maroon|wine)/.test(v)) return '#d32f2f'
    if (/(pink|rose|blush)/.test(v)) return '#ec407a'
    if (/(green|olive|sage|mint)/.test(v)) return '#2e7d32'
    if (/(brown|tan|beige|sand|nude)/.test(v)) return '#8d6e63'
    if (/(yellow|gold|mustard)/.test(v)) return '#ffcf33'
    if (/(purple|violet|plum)/.test(v)) return '#7b1fa2'
    return '#cfcfcf'
  }
  function buildOptions(product) {
    return (product.options || []).map((opt, idx) => {
      const position = idx + 1
      const name = typeof opt === 'string' ? opt : (opt && opt.name) || `Option ${position}`
      const seen = new Set(); const values = []
      for (const v of product.variants || []) {
        const val = v[`option${position}`]
        if (val != null && !seen.has(val)) { seen.add(val); values.push(val) }
      }
      return { name, position, values }
    })
  }
  function qaController(dialog) {
    const q = (s) => dialog.querySelector(s)
    const els = {
      title: q('[data-qa-title]'), thumb: q('[data-qa-thumb]'),
      prices: q('[data-qa-prices]'), options: q('[data-qa-options]'), atc: q('[data-qa-atc]'),
    }
    const cache = new Map()
    let product = null, options = [], selected = [], cur = '₹', inFlight = false

    function fetchProduct(handle) {
      if (!cache.has(handle)) {
        cache.set(handle, fetch(`/products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin',
        }).then((r) => { if (!r.ok) throw new Error('fetch ' + r.status); return r.json() }).catch((e) => { cache.delete(handle); throw e }))
      }
      return cache.get(handle)
    }
    function resolveVariant() {
      if (!product || selected.some((v) => v == null)) return null
      return (product.variants || []).find((v) => selected.every((sel, i) => v[`option${i + 1}`] === sel))
    }
    function variantImg(position, value) {
      const v = (product.variants || []).find((x) => x[`option${position}`] === value)
      const src = v && (v.featured_image?.src || (typeof v.featured_image === 'string' ? v.featured_image : null))
      if (!src) return null
      return src + (src.includes('?') ? '&' : '?') + 'width=120'
    }
    function renderThumb() {
      if (!els.thumb) return
      const v = resolveVariant()
      const src = (v && (v.featured_image?.src || (typeof v.featured_image === 'string' ? v.featured_image : null))) || product?.featured_image || product?.images?.[0]
      els.thumb.innerHTML = src ? `<img class="sai-fcbfrd03__qa-thumb-img" src="${esc(src)}${src.includes('?') ? '&' : '?'}width=300" alt="" loading="lazy" decoding="async">` : ''
    }
    function renderOptions() {
      if (!els.options) return
      els.options.innerHTML = options.map((opt) => {
        const idx = opt.position - 1
        const sel = selected[idx]
        const isColor = isColorOpt(opt.name)
        const vals = opt.values.map((value) => {
          const tentative = [...selected]; tentative[idx] = value
          const available = (product.variants || []).some((v) => tentative.every((s, i) => s == null || v[`option${i + 1}`] === s))
          const isSel = value === sel
          const attrs = `data-qa-opt="${opt.position}" data-qa-val="${esc(value)}"${available ? '' : ' aria-disabled="true"'} aria-pressed="${isSel ? 'true' : 'false'}"`
          if (isColor) {
            const img = variantImg(opt.position, value)
            const inner = img ? `<img class="sai-fcbfrd03__qa-swatch-img" src="${esc(img)}" alt="" loading="lazy" decoding="async">` : `<span class="sai-fcbfrd03__qa-swatch-dot" style="--qa-swatch:${COLOR_HEX(value)}"></span>`
            return `<button type="button" class="sai-fcbfrd03__qa-swatch${isSel ? ' is-sel' : ''}" ${attrs} title="${esc(value)}">${inner}</button>`
          }
          return `<button type="button" class="sai-fcbfrd03__qa-chip${isSel ? ' is-sel' : ''}" ${attrs}>${esc(value)}</button>`
        }).join('')
        return `<div class="sai-fcbfrd03__qa-option"><div class="sai-fcbfrd03__qa-opt-head"><span class="sai-fcbfrd03__qa-opt-name">${esc(opt.name)} :</span><strong class="sai-fcbfrd03__qa-opt-value">${esc(sel || '')}</strong></div><div class="sai-fcbfrd03__qa-values">${vals}</div></div>`
      }).join('')
    }
    function renderFooter() {
      const v = resolveVariant()
      if (!v) {
        if (els.prices) els.prices.innerHTML = '<span class="sai-fcbfrd03__qa-unavailable">Select options to continue</span>'
        if (els.atc) { els.atc.disabled = true; els.atc.textContent = 'Add to Cart' }
        return
      }
      const onSale = v.compare_at_price > v.price
      const pct = onSale ? Math.round(((v.compare_at_price - v.price) * 100) / v.compare_at_price) : 0
      if (els.prices) els.prices.innerHTML = `<span class="sai-fcbfrd03__qa-price-now">${fmtMinor(v.price, cur)}</span>` +
        (onSale ? `<span class="sai-fcbfrd03__qa-price-mrp">${fmtMinor(v.compare_at_price, cur)}</span>${pct > 0 ? `<span class="sai-fcbfrd03__qa-discount">${pct}% OFF</span>` : ''}` : '')
      if (els.atc) {
        if (!v.available) { els.atc.disabled = true; els.atc.textContent = 'Sold Out' }
        else if (inFlight) { els.atc.disabled = true; els.atc.textContent = 'Adding…' }
        else { els.atc.disabled = false; els.atc.textContent = 'Add to Cart' }
      }
    }
    function cartSectionIds() {
      const ids = new Set()
      document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => { if (el.dataset.sectionId) ids.add(el.dataset.sectionId) })
      return [...ids].slice(0, 5)
    }
    async function addToCart(variantId) {
      const body = { id: Number(variantId), quantity: 1 }
      const secs = cartSectionIds()
      if (secs.length) { body.sections = secs.join(','); body.sections_url = window.location.pathname }
      const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
      if (!res.ok) throw await res.json().catch(() => ({ message: 'Add to cart failed' }))
      const addJson = await res.json().catch(() => ({}))
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } })
      return { cart: cartRes.ok ? await cartRes.json() : null, sections: addJson?.sections || null }
    }
    function dispatchCart(cart, data) {
      const evt = new Event('cart:update', { bubbles: true })
      evt.detail = { resource: cart || {}, sourceId: 'sai-fcbfrd03-qa', data: { source: 'sai-fcbfrd03-qa', ...data } }
      document.dispatchEvent(evt)
    }
    async function atc() {
      const v = resolveVariant()
      if (!v || !v.available || inFlight) return
      inFlight = true; renderFooter()
      window.__spectrumQuickAddInFlight = true
      try {
        const { cart, sections } = await addToCart(v.id)
        dispatchCart(cart, { variantId: String(v.id), sections })
        close()
      } catch (e) { console.error('[sai-fcbfrd03-qa] atc failed', e) }
      finally { inFlight = false; renderFooter(); window.__spectrumQuickAddInFlight = false }
    }
    function close() {
      if (dialog.classList.contains('is-open')) { dialog.classList.remove('is-open'); setTimeout(() => { try { dialog.close() } catch (e) { dialog.removeAttribute('open') } }, 340) }
      else { try { dialog.close() } catch (e) { dialog.removeAttribute('open') } }
    }
    async function open(handle, currency) {
      if (!handle) return
      cur = currency || '₹'
      dialog.dataset.loading = 'true'
      const y = window.scrollY, prev = document.documentElement.style.scrollBehavior
      document.documentElement.style.scrollBehavior = 'auto'
      if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '')
      window.scrollTo(0, y); document.documentElement.style.scrollBehavior = prev
      requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-open')))
      try {
        product = await fetchProduct(handle)
        options = buildOptions(product)
        const first = (product.variants || []).find((v) => v.available) || product.variants?.[0]
        selected = options.map((o) => (first ? first[`option${o.position}`] : null))
        if (els.title) els.title.textContent = product.title || ''
        renderOptions(); renderFooter(); renderThumb()
      } catch (e) { console.error('[sai-fcbfrd03-qa] open failed', e); close() }
      finally { dialog.dataset.loading = 'false' }
    }
    // dialog-local interactions
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog || e.target.closest('[data-qa-close]')) { close(); return }
      const sw = e.target.closest('[data-qa-opt]')
      if (sw && !sw.hasAttribute('aria-disabled')) {
        const idx = Number(sw.dataset.qaOpt) - 1
        if (selected[idx] !== sw.dataset.qaVal) { selected[idx] = sw.dataset.qaVal; renderOptions(); renderFooter(); renderThumb() }
        return
      }
      if (e.target.closest('[data-qa-atc]')) atc()
    })
    dialog.addEventListener('cancel', (e) => { e.preventDefault(); close() })
    return { open, close }
  }

  const El = class extends HTMLElement {
    connectedCallback() {
      if (this._init) return
      this._init = true
      this._track = () => {}
      this._timers = []
    }
    disconnectedCallback() { this._clearTimers() }
    setAnalytics(t) { this._track = typeof t === 'function' ? t : () => {} }
    _clearTimers() { for (const t of this._timers) clearInterval(t); this._timers = [] }

    applyVariant(_content, pool) {
      this._pool = pool && typeof pool === 'object' ? pool : {}
      this._rows = buildRows(this._pool)
      if (!this._rows.length) return
      if (this._selected == null) {
        const bySel = this._rows.findIndex((r) => r.selected)
        const byCur = this._rows.findIndex((r) => r.current)
        this._selected = bySel >= 0 ? bySel : byCur >= 0 ? byCur : 0
      }
      this._render()
      this._bind()
      this._qaSetup()
    }

    _props() { return (this._pool && this._pool.props) || {} }

    // Handle of the currently-selected member (drives the mini-PDP drawer).
    _currentHandle() {
      const r = this._rows[this._selected]
      return (r && r.handle) || (this._pool.product && this._pool.product.handle) || null
    }

    // Wire the member mini-PDP drawer when prop member_quick_add is on. The
    // native Add-to-Cart lives in the theme's buy-buttons block (outside this
    // snippet); intercept it in the capture phase so our drawer opens for the
    // selected member instead of the theme adding the base product directly.
    _qaSetup() {
      if (!this._props().member_quick_add) return
      const dialog = this.parentNode && this.parentNode.querySelector('dialog[data-sai-fcbfrd03-qa]')
      if (!dialog) return
      if (!this._qa) this._qa = qaController(dialog)
      if (window.__sai_fcbfrd03_qa_bound__) return
      window.__sai_fcbfrd03_qa_bound__ = true
      // Only the MAIN product's Add-to-Cart (buybox block + sticky bar) — never a
      // product card / recommendation quick-add, our own drawer, or a member row.
      const ATC_SEL = '.buy-buttons-block [ref="addToCartButton"], .product-form-buttons [ref="addToCartButton"], sticky-add-to-cart [ref="addToCartButton"], .sticky-add-to-cart button[type="submit"]'
      const SKIP_SEL = 'dialog[data-sai-fcbfrd03-qa], ' + TAG + ', .card-product, product-card, .spectrum-plp-card, [data-spectrum-quick-add-trigger], [data-spectrum-direct-atc]'
      // Horizon's <add-to-cart-component>.handleClick fires the fly-to-cart + success
      // (checkmark) animation through a capture-phase event delegation registered at
      // theme load — before this snippet — so a capture stopImmediatePropagation can't
      // beat it. That animation is gated on the button's data-puppet flag, so mark the
      // native ATC a puppet: no native animation. The real add is the form submit, which
      // the capture-phase preventDefault() below cancels — so the native add never fires.
      const markPuppets = () => {
        for (const btn of document.querySelectorAll(ATC_SEL)) {
          if (!btn.closest(SKIP_SEL)) btn.dataset.puppet = 'true'
        }
      }
      markPuppets()
      // the sticky bar / a re-rendered buybox can mount after us — re-mark, debounced
      let _pt = null
      new MutationObserver(() => { if (_pt) return; _pt = setTimeout(() => { _pt = null; markPuppets() }, 250) })
        .observe(document.documentElement, { childList: true, subtree: true })
      document.addEventListener('click', (e) => {
        const btn = e.target.closest(ATC_SEL)
        if (!btn || btn.closest(SKIP_SEL)) return
        const host = document.querySelector(TAG)
        if (!host || !host._qa || !host._props().member_quick_add) return
        e.preventDefault(); e.stopImmediatePropagation()
        host._qa.open(host._currentHandle(), host._pool.product && host._pool.product.currency)
      }, true)
    }

    _label() {
      const p = this._props()
      if (p.label_prefix) return p.label_prefix
      if (this._pool.combo && this._pool.combo.name) return this._pool.combo.name.toUpperCase() + ' :'
      return 'COMBO :'
    }

    _timerGateOff() {
      // Placement kill-switch only. WHICH member ticks (and for how long) is
      // authored DATA — the combo config member's `timer` minutes — never a
      // product-level metafield. A member with no `timer` shows no clock.
      return this._props().show_timer === false
    }

    _render() {
      const props = this._props()
      const cur = this._pool.product.currency
      const selRow = this._rows[this._selected]
      // show_label config toggle: off = the rows sit directly under whatever
      // precedes them (matches live's combos that carry no heading of their own)
      const label = props.show_label === false
        ? ''
        : `<div class="sai-fcbfrd03__label"><span class="sai-fcbfrd03__label-key">${esc(this._label())}</span><span class="sai-fcbfrd03__label-val" data-sel-label>${esc(selRow ? selRow.title : '')}</span></div>`
      const timerGateOff = this._timerGateOff()

      let rows = '<div class="sai-fcbfrd03__rows">'
      for (let i = 0; i < this._rows.length; i++) {
        const r = this._rows[i]
        const selected = i === this._selected
        const badge = r.badge
          ? `<div class="sai-fcbfrd03__badge">${esc(r.badge).replace(/(\d+% OFF)/i, '<b>$1</b>')}</div>` : ''
        // SAVE only on the badged/default combo row (the component member rows
        // show just MRP + price) — matches Figma + the storefront.
        const showSave = props.show_save !== false && !!r.badge
        const save = r.save && showSave ? `<span class="sai-fcbfrd03__save">${props.save_label || 'SAVE'} ${fmtMoney(r.save, cur)}</span>` : ''
        const mrp = r.mrp && r.mrp > r.price ? `<span class="sai-fcbfrd03__mrp">${props.mrp_label || 'MRP'} <s>${fmtMoney(r.mrp, cur)}</s></span>` : ''
        const rowTimerMin = timerGateOff ? 0 : r.timerMin
        const showTimer = rowTimerMin > 0
        const timer = showTimer
          ? `<div class="sai-fcbfrd03__timer" data-timer-key="sai-frido-timer:${esc(this._pool.product.handle)}:combo:${i}" data-timer-min="${rowTimerMin}"><span class="sai-fcbfrd03__timer-label">${esc(props.timer_label || 'Limited Time Offer')}</span><span class="sai-fcbfrd03__timer-clock" data-clock></span></div>` : ''
        const thumb = r.image && props.show_thumb !== false ? `<img class="sai-fcbfrd03__row-thumb" src="${esc(r.image)}" alt="" loading="lazy" decoding="async">` : ''
        const rowInner = `<span class="sai-fcbfrd03__row-left">${thumb}<span class="sai-fcbfrd03__title${r.image ? ' sai-fcbfrd03__title--sm' : ''}">${esc(r.title)}</span></span>
          <span class="sai-fcbfrd03__row-right">${save}<span class="sai-fcbfrd03__prices">${mrp}<span class="sai-fcbfrd03__price">${fmtMoney(r.price, cur)}</span></span></span>`

        rows += `<div class="sai-fcbfrd03__unit">${badge}
          <button type="button" class="sai-fcbfrd03__row${selected ? ' is-sel' : ''}" data-action="pick" data-idx="${i}">${rowInner}</button>${timer}</div>`
      }
      rows += '</div>'

      this.innerHTML = `<div class="sai-fcbfrd03__inner">${label}${rows}</div>`
      this._startTimers()
    }

    _startTimers() {
      this._clearTimers()
      for (const el of this.querySelectorAll('[data-timer-key]')) {
        const key = el.dataset.timerKey
        const min = Number(el.dataset.timerMin) || 0
        if (!min) continue
        const clock = el.querySelector('[data-clock]')
        const tick = () => { if (clock) clock.innerHTML = fmtClock(timerState(key, min).remaining) }
        tick()
        this._timers.push(setInterval(tick, 1000))
      }
    }

    _bind() {
      if (this._clickBound) return
      this._clickBound = true
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-action]')
        if (!t) return
        if (t.dataset.action === 'pick') this._pick(Number(t.dataset.idx))
      })
    }

    _pick(i) {
      const row = this._rows[i]
      if (!row || i === this._selected) return
      this._selected = i
      this._render()
      // swap the active product IN PLACE — never navigate
      window.dispatchEvent(new CustomEvent('spectrum:buying-option-change', {
        detail: { variantId: row.variantId || null, title: row.title, price: row.price, mrp: row.mrp, image: row.image || null },
      }))
      this._track(`${FEATURE_SLUG}:select_combo`, { index: i, title: row.title })
    }
  }

  if (!customElements.get(TAG)) customElements.define(TAG, El)

  function bindAll() {
    const api = window.__spectrumAi && window.__spectrumAi.snippet
    for (const node of document.querySelectorAll(`[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`)) {
      const el = node.querySelector(TAG)
      if (!el) continue
      // First paint straight from the server-rendered pool — the pool's props
      // are the instance's default config, so rendering never waits on the
      // SDK (which may load after DOMContentLoaded or resolve asynchronously).
      if (!el._painted) {
        const initPool = readPool(node)
        if (initPool && initPool.combo) { el._painted = true; el.applyVariant({}, initPool) }
      }
      if (api && typeof api.bind === 'function' && !el._wrapBound) {
        el._wrapBound = true
        const handles = api.bind(node, (ctx) => {
          const e = node.querySelector(TAG)
          if (e) e.applyVariant(ctx.variant ? ctx.variant.content : {}, readPool(node))
        })
        if (handles) el.setAnalytics(handles.track)
      }
    }
    for (const el of document.querySelectorAll(TAG)) {
      if (el._applied || el._wrapBound || el.closest('[data-spectrum-instance-id]')) continue
      const parent = el.closest('[data-spectrum-lq-snippet]') || el.parentElement
      if (!parent) continue
      const pool = readPool(parent)
      if (pool && pool.combo) { el._applied = true; el.applyVariant({}, pool) }
    }
  }

  if (typeof globalThis !== 'undefined' && globalThis.__SAI_TEST_HARNESS__ === true) {
    globalThis.__saiFcbfrd03 = { buildRows, timerState, fmtClock, fmtMoney }
  }

  function schedule() {
    bindAll()
    // the SDK can load after us — retry until it binds (or give up quietly)
    let tries = 0
    const t = setInterval(() => {
      bindAll()
      const api = window.__spectrumAi && window.__spectrumAi.snippet
      if ((api && typeof api.bind === 'function') || ++tries > 20) clearInterval(t)
    }, 500)
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true })
  } else {
    schedule()
  }

  // The storefront SDK can re-mount slot wrappers after load (conflict
  // resolution / vis flips) — a replaced wrapper holds a fresh unpainted
  // element, so rebind on DOM changes, debounced.
  const __saiRelevant = (records) => {
    for (const r of records) {
      for (const n of r.addedNodes) {
        if (n.nodeType !== 1) continue
        if ((n.matches && n.matches(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"], [data-spectrum-lq-snippet]`)) ||
            (n.querySelector && n.querySelector(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"]`))) return true
      }
    }
    return false
  }
  const __saiMo = new MutationObserver((records) => {
    if (__saiMo._t || !__saiRelevant(records)) return
    __saiMo._t = setTimeout(() => { __saiMo._t = null; bindAll() }, 150)
  })
  __saiMo.observe(document.documentElement, { childList: true, subtree: true })
  setTimeout(function () { try { __saiMo.disconnect() } catch (e) {} }, 30000)
})()
